/**
 * React Query hooks for tree sharing operations
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import {
  addTreeMember,
  AddTreeMemberRequest,
  listTreeMembers,
  removeTreeMember,
  updateTreeMember,
  UpdateTreeMemberRequest,
} from "../api/trees";

/**
 * Hook to list tree members
 */
export function useTreeMembers(treeId: number | null) {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ["trees", treeId, "members"],
    queryFn: async () => {
      if (!treeId) throw new Error("No tree ID provided");
      const token = await getAccessToken();
      return listTreeMembers(treeId, token);
    },
    enabled: !!treeId,
  });
}

/**
 * Hook to add a tree member
 */
export function useAddTreeMember(treeId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddTreeMemberRequest) => {
      const token = await getAccessToken();
      return addTreeMember(treeId, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trees", treeId, "members"] });
    },
  });
}

/**
 * Hook to update a tree member's role
 */
export function useUpdateTreeMember(treeId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string;
      data: UpdateTreeMemberRequest;
    }) => {
      const token = await getAccessToken();
      return updateTreeMember(treeId, userId, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trees", treeId, "members"] });
    },
  });
}

/**
 * Hook to remove a tree member
 */
export function useRemoveTreeMember(treeId: number) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const token = await getAccessToken();
      return removeTreeMember(treeId, userId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trees", treeId, "members"] });
    },
  });
}

/**
 * Hook to delete a tree
 */
export function useDeleteTree() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (treeId: number) => {
      const token = await getAccessToken();
      const { deleteTree } = await import("../api/trees");
      return deleteTree(treeId, token);
    },
    onSuccess: () => {
      // Invalidate trees list to refresh
      queryClient.invalidateQueries({ queryKey: ["trees"] });
    },
  });
}
